/**
 * KeyPad Service - it provides necessary functionality for the keypad component
 * Do not use it elsewhere, not as provider for component
 * Call destroyKeyPad() method on onDestroy lifecycle hook
 *
 *
 * Note. In order to add support for the new type of keypad described in the KeyPadConfigSet set
 * of the KeyPadConfigService service and use sole component like this one
 * <keypad
 *   ...
 *   ...>
 * <keypad>
 * next steps should be done
 * 1. check and expand if necessary mappings in the mapKeyPadType method of KeyPadService class
 * 2. check and expand if necessary switch case statement in the method onKeypadValue of the KeypadComponent -
 *    this method defines type of the value which will be used inside the KeyPad class and which rules
 *    will be applied to check whether entered value is valid or not.
 * 3. Enjoy fully working new type of keypad
 */
import { Injectable } from '@angular/core';

import { KeyPad } from '@components/key-pad-module/components/keypad/classes/KeyPad';
import {
  KeypadAction,
  KeypadButtonAction,
  KeypadValueType,
} from '@components/key-pad-module/components/keypad/classes/KeyPad.enums';
import { KeypadConfigType } from '@components/key-pad-module/interfaces/key-pad-config.enum';

import type {
  KeypadButton,
  KeypadButtonsSet,
  KeypadEvent,
  KeypadOutputEvent,
} from '@components/key-pad-module/components/keypad/classes/KeyPad.type';
import type { KeypadDisplay } from '@components/key-pad-module/components/keypad/classes/KeypadDisplay';
import type { KeyPadConfig } from '@components/key-pad-module/interfaces/key-pad-config.type';
import type { Observable } from 'rxjs';

@Injectable()
export class KeyPadService {
  private static MONEY_PRECISION: number;
  private keyPad: KeyPad;

  public static createButtons(keypadType: KeypadConfigType): KeypadButton[] {
    const buttonsAmount = 9;
    const keyPadButtons: KeypadButton[] = [];

    for (let i = 1; i <= buttonsAmount; i += 1) {
      keyPadButtons.push({ value: `${i}`, action: KeypadButtonAction.PUSH });
    }
    const buttonsLastRow: KeypadButton[] =
      KeyPadService.getKeyPadLastRowButtons(keypadType);

    keyPadButtons.push(...buttonsLastRow);
    return keyPadButtons;
  }

  /**
   * Update display value on keypad event. It performs simple basic operations,
   * like push and pop.
   * @param {KeypadEvent} event
   * @param {KeypadDisplay} display
   * @returns {KeypadDisplay}
   */
  public static updateDisplayOnKeyPadEvent(
    event: KeypadEvent,
    display: KeypadDisplay,
  ): void {
    if (
      KeyPadService.isMaxValueExceeded(display) &&
      event.action === KeypadAction.PUSH
    ) {
      return;
    }

    if (event.action === KeypadAction.PUSH) {
      display.value += event.payload;
    } else {
      display.value = display.value.substr(0, display.value.length - 1);
    }
  }

  public static isValidInputLength(display: KeypadDisplay): boolean {
    return (
      display.value.length >= display.config.minChar &&
      display.value.length <= display.config.maxChar
    );
  }

  public static isMaxValueExceeded(display: KeypadDisplay): boolean {
    if (!display.shouldValidateMaxLength) {
      return false;
    }

    return display.value.length >= display.config.maxChar;
  }

  public static getKeyPadLastRowButtons(
    keypadType: KeypadConfigType,
  ): KeypadButton[] {
    const buttons: KeypadButtonsSet = {
      weightButtons: [
        { action: KeypadButtonAction.PUSH, value: '00' },
        { action: KeypadButtonAction.PUSH, value: '0' },
        { action: KeypadButtonAction.PUSH, value: '.' },
      ],
      currencyButtons: [
        { action: KeypadButtonAction.PUSH, value: '00' },
        { action: KeypadButtonAction.PUSH, value: '0' },
        {
          action: KeypadButtonAction.PUSH,
          value: '.',
          disabled: !KeyPadService.MONEY_PRECISION,
        },
      ],
      defaultButtons: [
        { action: KeypadButtonAction.BLANK, value: '' },
        { action: KeypadButtonAction.PUSH, value: '0' },
        { action: KeypadButtonAction.BLANK, value: '' },
      ],
    };

    switch (keypadType) {
      case KeypadConfigType.WEIGHT:
        return buttons.weightButtons;
      default:
        return buttons.defaultButtons;
    }
  }

  public constructor() {
    this.keyPad = new KeyPad();
  }

  public initializeKeyPad(config: KeyPadConfig): void {
    KeyPadService.MONEY_PRECISION = 2;
    this.keyPad.initialize({
      keyPadType: this.mapKeyPadType(config.kpType),
      length: {
        min: config.minChar,
        max: config.maxChar,
      },
      precision: config.precision || KeyPadService.MONEY_PRECISION,
      defaultMinVal: config.defaultMinVal,
      defaultVal: config.defaultVal,
      supportsAlphabeticSymbols: config.supportsAlphabeticSymbols,
    });
  }

  public setKeyPadValue(
    value = 0,
    returnInitialValue = false,
    enforced = false,
  ) {
    if (this.keyPad.isNewValue(value) || enforced) {
      this.keyPad.setValue(value, returnInitialValue);
      this.keyPad.updateState();
    }
  }

  public getKeyPadState(): Observable<KeypadOutputEvent> {
    return this.keyPad.state.asObservable();
  }

  public onKeyPadEvent(event: KeypadEvent): void {
    this.keyPad.onKeyPadEvent(event);
  }

  public resetInitialValue(): void {
    this.keyPad.resetInitialValue();
  }

  public destroyKeyPad(): void {
    this.keyPad.destroy();
  }

  public getNumberValue(value: string): number {
    return this.keyPad.getNumberValue(value);
  }

  private mapKeyPadType(keyPadConfigType: string): KeypadValueType {
    switch (keyPadConfigType) {
      case KeypadConfigType.WEIGHT: {
        return KeypadValueType.FLOAT;
      }
      default: {
        return KeypadValueType.INTEGER;
      }
    }
  }
}
